/** Shared furniture for the reference tabs, so all four read the same way. */
import React from 'react';
import { Box, LinearProgress, Tooltip, Typography } from '@mui/material';
import { IntelSection } from './intel';

export const GOLD = '#f5b942';
export const WARN = '#e0a33d';
export const BAD = '#e0563d';

export const DockAction: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  children: React.ReactNode;
}> = ({ onClick, disabled, title, children }) => (
  <Tooltip title={title || ''} disableHoverListener={!title}>
    <Typography
      component="span"
      role="button"
      tabIndex={disabled ? -1 : 0}
      variant="caption"
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      }}
      sx={{
        fontWeight: 700,
        whiteSpace: 'nowrap',
        cursor: disabled ? 'default' : 'pointer',
        color: disabled ? 'text.disabled' : GOLD,
        '&:hover': disabled ? undefined : { textDecoration: 'underline' },
        '&:focus-visible': { outline: `1px solid ${GOLD}`, outlineOffset: 2 },
      }}
    >
      {children}
    </Typography>
  </Tooltip>
);

export const TabHeader: React.FC<{
  title: string;
  hint?: string;
  actions?: React.ReactNode;
  help?: React.ReactNode;
}> = ({ title, hint, actions, help }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
    <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.3 }}>
      {title}
    </Typography>
    {help}
    {hint && (
      <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
        {hint}
      </Typography>
    )}
    <Box sx={{ flex: 1 }} />
    {actions}
  </Box>
);

export const EmptyState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{ display: 'block', lineHeight: 1.45, mt: 0.25 }}
  >
    {children}
  </Typography>
);

export const Loading: React.FC<{ label: string }> = ({ label }) => (
  <Box sx={{ py: 0.5 }}>
    <LinearProgress sx={{ height: 2, mb: 0.5, '& .MuiLinearProgress-bar': { bgcolor: GOLD } }} />
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Box>
);

export const ErrorLine: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" sx={{ display: 'block', color: BAD, mt: 0.5 }}>
    {children}
  </Typography>
);

function ago(iso: string | null): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  return `${Math.round(hours / 24)} d ago`;
}

/**
 * Says where a held report came from and whether it still applies.
 *
 * Shown whenever a section is not describing the live route, which is the whole
 * point of keeping it: an out-of-date list is far more useful than no list, but
 * only if it is labelled as out of date.
 */
export const Provenance: React.FC<{
  section: IntelSection<any>;
  onRefresh?: () => void;
}> = ({ section, onRefresh }) => {
  if (!section.data) return null;
  const when = ago(section.capturedAt);
  if (!section.stale && !section.persisted) {
    return when ? (
      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 0.5, fontSize: '0.62rem', color: 'text.disabled' }}
      >
        Scanned {when}
      </Typography>
    ) : null;
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 0.5 }}>
      <Typography variant="caption" sx={{ fontSize: '0.62rem', color: WARN, flex: 1 }}>
        {section.stale
          ? `For an earlier version of this route${when ? `, ${when}` : ''}`
          : `Saved with the mission${when ? ` ${when}` : ''}`}
      </Typography>
      {onRefresh && <DockAction onClick={onRefresh}>Rescan</DockAction>}
    </Box>
  );
};
